import { calculateConsecutiveDaysBasedOnBudgetLength } from 'src/components/oneCorp/features/MealCredit/utils/calculateConsecutiveDaysBasedOnBudgetLength';
import { fromUTC } from 'src/utilities/fromUTC';
import { addMinutesToUtcDate } from 'src/utilities/addMinutesToUtcDate';
import { getBudgetLength } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';
import { IMealCredit } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseMealCreditsDetailsPage.utils';
import { getDurationsSummaryInfo } from 'src/components/oneCorp/features/MealCredit/utils/getDurationsSummaryInfo';
import {
  calculateDateFields,
  calculateExpirationFields,
} from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/calculateDateFields';
import { getValidDaysOfWeekBooleans } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getValidDaysOfWeekBooleans';
import {
  getBudgetResetType,
  getFormattedBudgetResetPeriod,
} from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/mealCreditBudget';
import { calculateMonthAvailableTo } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/calculateMonthAvailableTo';
import { getBudgetLengthDetails } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getBudgetLengthDetails';
import { getInstanceMonthAvailability } from 'src/components/oneCorp/features/MealCredit/utils/mealCreditDataUtils/getInstanceMonthAvailability';
import { calculateConsecutiveMonthsBasedOnBudgetLength } from 'src/components/oneCorp/features/MealCredit/utils/calculateConsecutiveMonthsBasedOnBudgetLength';

export interface UseGetDurationsSummary {
  durationsSummary: string;
}

export const useGetDurationsSummary = (
  mealCredit: IMealCredit
): UseGetDurationsSummary => {
  const {
    first_instance_utc: firstInstanceUtc,
    duration_minutes: durationMinutes,
    repeating: isRepeating,
    valid_days_of_week: validDaysOfWeek,
    budget_reset_period: budgetResetPeriod,
    expiration,
    first_instance_end_date_utc: firstInstanceEndDateUtc,
    instance_type: instanceType,
  } = mealCredit;

  const budgetLength = getBudgetLength(mealCredit);
  const budgetResetType = getBudgetResetType(instanceType, budgetResetPeriod);
  const {
    isDailyLengthBudget,
    isWeeklyLengthBudget,
    isMonthlyLengthBudget,
    isMonthAvailable,
  } = getBudgetLengthDetails(budgetLength, budgetResetType);
  const formattedBudgetResetPeriod = Number(
    getFormattedBudgetResetPeriod(instanceType, budgetResetPeriod)
  );
  const startDate = fromUTC(firstInstanceUtc);
  const selectedDays = getValidDaysOfWeekBooleans(validDaysOfWeek);

  const endTimeFullDate = addMinutesToUtcDate(
    firstInstanceUtc,
    durationMinutes
  );
  const refreshPeriodDaysCount = calculateConsecutiveDaysBasedOnBudgetLength({
    budgetLength,
    budgetResetPeriod: formattedBudgetResetPeriod,
    budgetResetType,
  });

  const refreshPeriodMonthsCount =
    calculateConsecutiveMonthsBasedOnBudgetLength({
      budgetLength,
      budgetResetPeriod: formattedBudgetResetPeriod,
      budgetResetType,
    });

  const { timeIntervalType } = calculateDateFields({
    firstInstanceUtc,
    durationMinutes,
  });

  const { expiresOn, expirationDate } = calculateExpirationFields({
    expiration,
    timeIntervalType,
  });

  const monthAvailableTo = isMonthAvailable
    ? calculateMonthAvailableTo({
        firstInstanceUtc,
        firstInstanceEndDateUtc,
        refreshPeriodMonthsCount,
        durationMinutes,
      })
    : null;
  const monthAvailableOn = getInstanceMonthAvailability({
    firstInstanceUtc,
    firstInstanceEndDateUtc,
    timeIntervalType,
  });

  const durationsSummary = getDurationsSummaryInfo({
    isDailyLengthBudget,
    isWeeklyLengthBudget,
    isMonthlyLengthBudget,
    isMonthAvailable,
    budgetLength,
    isRepeating,
    startDate,
    selectedDays,
    budgetResetType,
    budgetResetPeriod: formattedBudgetResetPeriod,
    expirationDate,
    refreshPeriodDaysCount,
    refreshPeriodMonthsCount,
    endTimeFullDate,
    expiresOn,
    timeIntervalType,
    durationMinutes,
    monthAvailableTo,
    monthAvailableOn,
  });

  return {
    durationsSummary,
  };
};
